import {
  HHAlert,
  HHButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useHingeSelectClient } from '../../api';
import { Bundle } from '../../api/types';

const monoCell = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontVariantNumeric: 'tabular-nums' as const,
  fontSize: 13,
};

export interface BundlePickerProps {
  disabledIds: Set<string>;
  onSelect: (bundle: Bundle) => void;
}

export const BundlePicker = ({
  disabledIds,
  onSelect,
}: BundlePickerProps): JSX.Element => {
  const client = useHingeSelectClient();
  const [status, setStatus] = useState<'pending' | 'loaded' | 'error'>(
    'pending',
  );
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    void (async (): Promise<void> => {
      try {
        const res = await client.listBundles({
          paginationParams: { pageSize: 100 },
        });
        setBundles(res.bundles ?? []);
        setStatus('loaded');
      } catch (e) {
        setError((e as Error).message);
        setStatus('error');
      }
    })();
  }, [client]);

  if (status === 'pending') {
    return (
      <HHStack>
        <HHTypography hhVariant="body2" sx={{ opacity: 0.7 }}>
          Loading bundles…
        </HHTypography>
      </HHStack>
    );
  }

  if (status === 'error') {
    return (
      <HHAlert hhVariant="outlined" severity="error">
        Failed to load bundles: {error}
      </HHAlert>
    );
  }

  if (bundles.length === 0) {
    return (
      <HHStack>
        <HHTypography hhVariant="body2">No bundles found.</HHTypography>
      </HHStack>
    );
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ maxHeight: 320 }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell sx={{ width: 220 }}>Type</TableCell>
            <TableCell sx={{ width: 320 }}>Bundle ID</TableCell>
            <TableCell sx={{ width: 80 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {bundles.map(b => {
            const disabled = b.id != null && disabledIds.has(b.id);
            return (
              <TableRow
                key={b.id ?? b.name}
                sx={{ opacity: disabled ? 0.45 : 1 }}
              >
                <TableCell>{b.name}</TableCell>
                <TableCell sx={monoCell}>{b.type}</TableCell>
                <TableCell sx={monoCell}>{b.id ?? '—'}</TableCell>
                <TableCell align="right">
                  <HHButton
                    hhVariant="variant-bypass"
                    variant="outlined"
                    size="small"
                    disabled={disabled}
                    onClick={(): void => {
                      if (b.id != null) onSelect(b);
                    }}
                  >
                    {disabled ? 'Added' : 'Add'}
                  </HHButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
